/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';

interface Tube {
  id: string;
  productionSchedule: string;
  productName: string;
  serialNumber: string;
  batchCode: string;
}

interface SterilityTestingInitiationProps {
  onCancel: () => void;
  onSubmit: (data: any) => void;
}

const SterilityTestingInitiation: React.FC<SterilityTestingInitiationProps> = ({ onCancel, onSubmit }) => {
  const [tubes, setTubes] = useState<Tube[]>([]);
  const [manualSerial, setManualSerial] = useState('');
  const [testMethod, setTestMethod] = useState<'Direct' | 'Membrane'>('Direct');
  const [validationStatus, setValidationStatus] = useState({
    mediaValid: true,
    notExpired: true,
    qaApproved: true
  });

  const [membraneData, setMembraneData] = useState({
    filtrationDone: 'No',
    filterId: '',
    washCycles: '',
    remarks: ''
  });

  const handleAddTube = () => {
    if (!manualSerial) return;
    
    // Mock validation logic
    if (manualSerial.startsWith('ERR')) {
      alert('Invalid QR / Serial Number entered.');
      return;
    }

    const newTube: Tube = {
      id: Math.random().toString(36).substr(2, 9),
      productionSchedule: 'PS-2024-X1',
      productName: 'Tryptic Soy Broth (TSB)',
      serialNumber: manualSerial,
      batchCode: 'LOT-2023-X9'
    };
    setTubes([...tubes, newTube]);
    setManualSerial('');
  };

  const removeTube = (id: string) => {
    setTubes(tubes.filter(t => t.id !== id));
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-8 pb-20 media-prep-scope">
      {/* Header Info Bar */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 flex items-center justify-between mb-4">
        <div className="flex gap-12">
            <button 
            onClick={onCancel}
            className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#0F172A] hover:bg-slate-50 transition-all shadow-sm"
            >
            <span className="material-symbols-outlined text-xl">arrow_back</span>
            </button>
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Activity ID</p>
            <p className="text-xl font-black text-slate-800 tracking-tight">ST-2024-0812</p>
          </div>
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Assigned User</p>
            <p className="text-sm font-bold text-slate-700">P Shyam Kumar</p>
          </div>
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Schedule Date</p>
            <p className="text-sm font-bold text-slate-700">12-Aug-2024</p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-4 py-1.5 bg-blue-50 text-blue-600 rounded-full border border-blue-100">
          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
          <span className="text-[10px] font-black uppercase tracking-widest">Ready</span>
        </div>
      </div>

      <div className="space-y-8 my-2">
        {/* Row 1: Media Tube Selection (Full Width) */}
        <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-8 py-6 border-b border-slate-50 flex items-center gap-3">
            <span className="material-symbols-outlined text-slate-400">qr_code_scanner</span>
            <h2 className="text-[12px] font-black text-slate-800 uppercase tracking-widest">Media Tube Selection</h2>
          </div>
          
          <div className="p-8 space-y-8">
            <div className="grid grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">QR/Barcode Scanner</label>
                <div className="relative group">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-300 group-focus-within:text-blue-500 transition-colors">qr_code_2</span>
                  <input 
                    type="text" 
                    placeholder="Scan Media Tube QR Code..."
                    className="w-full bg-slate-50 border border-slate-100 rounded-xl py-3.5 pl-12 pr-4 text-sm font-medium focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/5 outline-none transition-all"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Manual Serial Entry</label>
                <div className="flex gap-2">
                  <div className="relative flex-1 group">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-300 group-focus-within:text-blue-500 transition-colors">edit_note</span>
                    <input 
                      type="text" 
                      value={manualSerial}
                      onChange={(e) => setManualSerial(e.target.value)}
                      placeholder="Enter serial number..."
                      className="w-full bg-slate-50 border border-slate-100 rounded-xl py-3.5 pl-12 pr-4 text-sm font-medium focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/5 outline-none transition-all"
                    />
                  </div>
                  <button 
                    onClick={handleAddTube}
                    className="px-8 bg-[#0F172A] text-white rounded-xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all active:scale-95"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Tubes Table */}
            <div className="border border-slate-100 rounded-2xl overflow-hidden shadow-sm">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-50/50">
                    <th className="px-6 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Production Schedule</th>
                    <th className="px-6 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Product Name</th>
                    <th className="px-6 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Serial Number</th>
                    <th className="px-6 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Batch Code</th>
                    <th className="px-6 py-4 text-center text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Remove</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <AnimatePresence>
                    {tubes.map(tube => (
                      <motion.tr 
                        key={tube.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className="hover:bg-slate-50/50 transition-colors"
                      >
                        <td className="px-6 py-4 text-xs font-bold text-slate-600 tracking-tight">{tube.productionSchedule}</td>
                        <td className="px-6 py-4 text-xs font-black text-slate-700">{tube.productName}</td>
                        <td className="px-6 py-4 text-xs font-bold text-blue-600 font-mono italic">{tube.serialNumber}</td>
                        <td className="px-6 py-4 text-xs font-bold text-slate-500">{tube.batchCode}</td>
                        <td className="px-6 py-4 text-center">
                          <button 
                            onClick={() => removeTube(tube.id)}
                            className="w-8 h-8 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all"
                          >
                            <span className="material-symbols-outlined text-lg">delete</span>
                          </button>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                  {tubes.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center gap-2 text-slate-300">
                          <span className="material-symbols-outlined text-4xl">inventory_2</span>
                          <p className="text-xs font-bold italic">No media tubes selected</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Row 2: Test Method & Pre-validation (Two Columns) */}
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-8">
            <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden h-full">
              <div className="px-8 py-6 border-b border-slate-50 flex items-center gap-3">
                <span className="material-symbols-outlined text-slate-400">dataset</span>
                <h2 className="text-[12px] font-black text-slate-800 uppercase tracking-widest">Test Method Selection</h2>
              </div>
              
              <div className="p-8 space-y-10">
                <div className="grid grid-cols-2 gap-6">
                  <button 
                    onClick={() => setTestMethod('Direct')}
                    className={`p-6 rounded-[2rem] border-2 transition-all text-left flex items-start gap-4 ${
                      testMethod === 'Direct' 
                      ? 'border-orange-500 bg-orange-50/20' 
                      : 'border-slate-100 hover:border-slate-200 bg-white'
                    }`}
                  >
                    <div className={`mt-1 h-4 w-5 rounded-full border-2 flex items-center justify-center ${
                      testMethod === 'Direct' ? 'border-orange-500' : 'border-slate-300'
                    }`}>
                      {testMethod === 'Direct' && <div className="h-2.5 w-2.5 bg-orange-500 rounded-full" />}
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-black text-slate-800 uppercase">Direct Inoculation</p>
                      <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                        Suitable for simple liquid samples or smaller volumes where direct contact is preferred.
                      </p>
                      <div className="flex items-center gap-1.5 pt-2">
                         <span className="material-symbols-outlined text-xs text-blue-500 font-black">info</span>
                         <span className="text-[9px] font-black text-blue-600 uppercase tracking-widest">Standard Protocol</span>
                      </div>
                    </div>
                  </button>

                  <button 
                    onClick={() => setTestMethod('Membrane')}
                    className={`p-6 rounded-[2rem] border-2 transition-all text-left flex items-start gap-4 ${
                      testMethod === 'Membrane' 
                      ? 'border-orange-500 bg-orange-50/20' 
                      : 'border-slate-100 hover:border-slate-200 bg-white'
                    }`}
                  >
                    <div className={`mt-1 h-4 w-5 rounded-full border-2 flex items-center justify-center ${
                      testMethod === 'Membrane' ? 'border-orange-500' : 'border-slate-300'
                    }`}>
                      {testMethod === 'Membrane' && <div className="h-2.5 w-2.5 bg-orange-500 rounded-full" />}
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-black text-slate-800 uppercase">Membrane Filtration</p>
                      <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                        Best for large volumes or samples containing antimicrobial agents that require rinsing.
                      </p>
                      <div className="flex items-center gap-1.5 pt-2">
                         <span className="material-symbols-outlined text-xs text-blue-500 font-black">info</span>
                         <span className="text-[9px] font-black text-blue-600 uppercase tracking-widest">Enhanced Sensitivity</span>
                      </div>
                    </div>
                  </button>
                </div>

                <AnimatePresence>
                  {testMethod === 'Membrane' && (
                    <motion.div 
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden space-y-8 pt-4 border-t border-slate-50"
                    >
                      <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Filtration Done?</label>
                          <div className="flex p-1 bg-slate-100 rounded-xl w-fit">
                            {['Yes', 'No'].map(opt => (
                              <button
                                key={opt}
                                onClick={() => setMembraneData({...membraneData, filtrationDone: opt})}
                                className={`px-6 py-2 rounded-lg text-[11px] font-black transition-all ${
                                  membraneData.filtrationDone === opt ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'
                                }`}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Filter/Membrane ID</label>
                          <input 
                            type="text" 
                            value={membraneData.filterId}
                            onChange={(e) => setMembraneData({...membraneData, filterId: e.target.value})}
                            placeholder="MF-XXXXX-XXXX"
                            className="w-full bg-slate-50 border border-slate-100 rounded-xl py-3.5 px-4 text-sm font-medium focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/5 outline-none transition-all"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">No. of Wash Cycles</label>
                          <input 
                            type="number" 
                            value={membraneData.washCycles}
                            onChange={(e) => setMembraneData({...membraneData, washCycles: e.target.value})}
                            placeholder="3"
                            className="w-full bg-slate-50 border border-slate-100 rounded-xl py-3.5 px-4 text-sm font-medium focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/5 outline-none transition-all"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Remarks</label>
                        <textarea 
                          value={membraneData.remarks}
                          onChange={(e) => setMembraneData({...membraneData, remarks: e.target.value})}
                          placeholder="Additional observations..."
                          rows={2}
                          className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3.5 px-4 text-sm font-medium focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/5 outline-none transition-all resize-none"
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          <div className="col-span-4">
            <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-8 space-y-8 h-full">
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-slate-400">checklist</span>
                <h2 className="text-[12px] font-black text-slate-800 uppercase tracking-widest">Pre-validation Checklist</h2>
              </div>

              <div className="space-y-3">
                {[
                  { label: 'Media Valid', sub: 'ID-OK', status: validationStatus.mediaValid },
                  { label: 'Not Expired', sub: 'DTE-OK', status: validationStatus.notExpired },
                  { label: 'QA Approved', sub: 'SIG-OK', status: validationStatus.qaApproved }
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${item.status ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                        <span className="material-symbols-outlined text-base font-black">{item.status ? 'check' : 'close'}</span>
                      </div>
                      <span className="text-[11px] font-black text-slate-700 uppercase tracking-tight">{item.label}</span>
                    </div>
                    <span className="text-[10px] font-mono text-slate-400 font-black">{item.sub}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Row 3: Actions (Third Row) */}
        <div className="flex flex-col items-center gap-6 pt-4">
          <div className="flex gap-4 w-full max-w-2xl">
            <button 
              onClick={() => onSubmit({ tubes, testMethod, membraneData })}
              className="flex-1 bg-orange-500 text-white rounded-2xl py-3 text-sm font-black uppercase tracking-widest shadow-xl shadow-orange-500/20 hover:brightness-110 active:scale-95 transition-all text-center"
            >
              Start Activity
            </button>
            <button 
              onClick={onCancel}
              className="flex-1 bg-white border border-slate-200 text-slate-600 rounded-2xl py-3 text-sm font-black uppercase tracking-widest hover:bg-slate-50 transition-all text-center"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SterilityTestingInitiation;
